import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, BACKEND_URL, CATEGORY_NAME, fmtDuration } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { PortalShell } from "@/pages/ClientPortalShell";
import { BuyButtons } from "@/pages/Marketplace";
import { Download, Play, Film, X, Search, Lock, ShoppingCart, Check, Share2, Pencil, Sparkles, ArrowRight, Zap, Shield, Building2, Calendar, Presentation } from "lucide-react";
import { toast } from "sonner";
import { ShareSheet } from "@/components/ShareSheet";
import { openInCapCut } from "@/lib/openInCapCut";
import CoachingModal from "@/components/CoachingModal";
import CoachingBanner from "@/components/CoachingBanner";

export default function ClientMyClips() {
  const { user, tier } = useAuth();
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [linkedRiders, setLinkedRiders] = useState([]);
  const [linkedHorses, setLinkedHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [previewClip, setPreviewClip] = useState(null);
  const [shareClip, setShareClip] = useState(null);
  const [coachClip, setCoachClip] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pricing, setPricing] = useState({
    default_price: 10,
    currency: "cad",
    watermark_text: "Property of Elevated Arena",
    preview_seconds: 5,
  });
  const [buyingId, setBuyingId] = useState(null);
  const [subInfo, setSubInfo] = useState(null); // {tier, storage_used_bytes, storage_limit_bytes}
  const [myAssociations, setMyAssociations] = useState([]);

  useEffect(() => {
    api.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/subscriptions/me").then((r) => setSubInfo(r.data)).catch(() => {});
    // "My Associations" — only present when the admin has explicitly
    // linked this user to one or more associations. Empty otherwise.
    api.get("/me/associations").then((r) => setMyAssociations(r.data.associations || [])).catch(() => {});
    api
      .get("/portal/my-clips")
      .then((r) => {
        setClips(r.data.clips || []);
        setLinkedRiders(r.data.linked_riders || []);
        setLinkedHorses(r.data.linked_horses || []);
      })
      .catch(() => toast.error("Failed to load your clips"))
      .finally(() => setLoading(false));
  }, []);

  const buyClip = async (clip) => {
    if (buyingId) return;
    setBuyingId(clip.id);
    try {
      const r = await api.post("/payments/checkout", {
        clip_id: clip.id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to start checkout";
      toast.error(msg);
      setBuyingId(null);
    }
  };

  // Editing a purchased clip → server-side copies it into the buyer's Rodeo Box
  // then navigates to the Box studio editor. Requires Creator+ (d2).
  const editClip = async (clip) => {
    if (editingId) return;
    setEditingId(clip.id);
    try {
      const r = await api.post("/box/import-purchased-clip", { clip_id: clip.id });
      toast.success("Copied into your Rodeo Box — opening editor…");
      navigate(`/portal/studio/${r.data.video_id}`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Failed to open editor";
      if (status === 402) {
        toast.error(msg, {
          description: "Editing is a Creator/Pro feature.",
          action: { label: "Upgrade", onClick: () => navigate("/portal/upgrade") },
        });
      } else {
        toast.error(msg);
      }
      setEditingId(null);
    }
  };

  // Paid CapCut export — gated to Creator+ tier (matches other editing features)
  const TIER_RANK = { free: 0, storage: 1, creator: 2, pro: 3 };
  // Tier comes from AuthContext (which fetches /subscriptions/me once), not
  // from /auth/me — the auth response doesn't include subscription_tier.
  const currentTier = tier || user?.subscription_tier || "free";
  const hasCapCutAccess = TIER_RANK[currentTier] >= TIER_RANK.creator;
  const canCoach = TIER_RANK[currentTier] >= TIER_RANK.pro;
  const handleCapCut = (clipId) => {
    if (!hasCapCutAccess) {
      toast.error("CapCut export is a Creator/Pro feature", {
        description: "Unlock it (plus editing & reel building) by upgrading.",
        action: { label: "Upgrade", onClick: () => navigate("/portal/upgrade") },
      });
      return;
    }
    openInCapCut(clipId, "clip");
  };

  const priceLabel = useMemo(() => {
    const cur = (pricing.currency || "cad").toUpperCase();
    return `$${Number(pricing.default_price || 0).toFixed(2)} ${cur}`;
  }, [pricing]);

  const categories = useMemo(() => {
    const s = new Set(clips.map((c) => c.category).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [clips]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clips.filter((c) => {
      if (category !== "all" && c.category !== category) return false;
      if (!q) return true;
      const hay = [
        c.rider_horse_name,
        c.horse_name,
        c.heeler_name,
        c.sub_label,
        c.notes,
        c.event_title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [clips, query, category]);

  return (
    <PortalShell>
      <div className="border-l-4 border-orange-600 pl-5 mb-8" data-testid="my-clips-header">
        <div className="label-tiny mb-2">Your Clips</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">
          Welcome{user?.name ? `, ${user.name}` : ""}
        </h1>
        <p className="text-stone-400 mt-3">
          Preview your runs free for {pricing.preview_seconds} seconds. Buy any clip for{" "}
          <span className="text-orange-400 font-mono-tc">{priceLabel}</span> to unlock the full,
          watermark-free download forever.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-4">
          {linkedRiders.map((r) => (
            <span
              key={`r-${r}`}
              className="inline-flex items-center gap-1 border border-orange-700/60 bg-orange-600/10 text-orange-300 px-2 py-0.5 text-[11px] uppercase tracking-widest"
              data-testid={`linked-rider-${r}`}
            >
              Rider · {r}
            </span>
          ))}
          {linkedHorses.map((h) => (
            <span
              key={`h-${h}`}
              className="inline-flex items-center gap-1 border border-stone-700 bg-stone-900 text-stone-300 px-2 py-0.5 text-[11px] uppercase tracking-widest"
              data-testid={`linked-horse-${h}`}
            >
              Horse · {h}
            </span>
          ))}
          {linkedRiders.length === 0 && linkedHorses.length === 0 && myAssociations.length === 0 && (
            <span className="text-stone-500 text-xs">
              No riders or horses linked to your account yet. Ask the studio team to link them.
            </span>
          )}
        </div>
      </div>

      <BuyButtons />

      {myAssociations.length > 0 && (
        <div className="mb-8" data-testid="portal-my-associations">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={16} className="text-orange-500" />
            <div className="font-display uppercase text-lg">My Associations</div>
            <span className="text-[10px] uppercase tracking-widest text-stone-500">{myAssociations.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {myAssociations.map((a) => (
              <Link
                key={a.name}
                to={`/associations/${encodeURIComponent(a.name)}`}
                className="border border-orange-900/40 bg-orange-950/10 hover:bg-orange-950/30 hover:border-orange-700 transition-colors p-4 group"
                data-testid={`portal-association-${a.name.replace(/\s+/g, "_")}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-display uppercase font-semibold text-base truncate group-hover:text-orange-400">{a.name}</div>
                  <ArrowRight size={14} className="text-stone-500 group-hover:text-orange-500 mt-1 shrink-0" />
                </div>
                <div className="text-xs text-stone-400 mt-2 font-mono-tc flex flex-wrap gap-x-3">
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {a.video_count} videos</span>
                  <span className="inline-flex items-center gap-1"><Film size={11} /> {a.clip_count} clips</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <PlanPickerBanner tier={user?.subscription_tier || "free"} subInfo={subInfo} onGo={() => navigate("/portal/upgrade")} />

      {canCoach && (
        <CoachingBanner
          clips={clips}
          onTry={(c) => setCoachClip(c)}
          uploadHref="/portal/studio"
        />
      )}

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : clips.length === 0 ? (
        <div
          className="border border-dashed border-stone-800 p-16 text-center"
          data-testid="my-clips-empty"
        >
          <Film size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">
            {linkedRiders.length === 0 && linkedHorses.length === 0
              ? "Your account isn't linked to a rider or horse yet."
              : "No clips for your riders or horses yet. Check back soon."}
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search rider, horse, notes…"
                className="field-input w-full pl-8"
                data-testid="my-clips-search"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="field-input"
              data-testid="my-clips-category"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "All events" : CATEGORY_NAME[c] || c}
                </option>
              ))}
            </select>
            <div className="font-mono-tc text-xs text-stone-500 ml-auto">
              <span className="text-orange-500 text-base">{filtered.length}</span> of {clips.length}
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="my-clips-grid"
          >
            {filtered.map((c) => (
              <ClipCard
                key={c.id}
                clip={c}
                pricing={pricing}
                priceLabel={priceLabel}
                busy={buyingId === c.id}
                editing={editingId === c.id}
                hasCapCutAccess={hasCapCutAccess}
                canCoach={canCoach}
                onPreview={() => setPreviewClip(c)}
                onBuy={() => buyClip(c)}
                onShare={() => setShareClip(c)}
                onEdit={() => editClip(c)}
                onCapCut={() => handleCapCut(c.id)}
                onCoach={() => canCoach ? setCoachClip(c) : navigate("/portal/upgrade")}
              />
            ))}
          </div>
        </>
      )}

      {previewClip && (
        <PreviewModal
          clip={previewClip}
          pricing={pricing}
          priceLabel={priceLabel}
          busy={buyingId === previewClip.id}
          onClose={() => setPreviewClip(null)}
          onBuy={() => buyClip(previewClip)}
        />
      )}
      {shareClip && (
        <ShareSheet
          clipId={shareClip.id}
          title={shareClip.rider_horse_name || shareClip.sub_label || "My rodeo clip"}
          downloadUrl={`${BACKEND_URL}/api/clips/${shareClip.id}/download`}
          onClose={() => setShareClip(null)}
        />
      )}
      {coachClip && (
        <CoachingModal
          clip={coachClip}
          onClose={() => setCoachClip(null)}
          onSaved={() => setCoachClip(null)}
        />
      )}
    </PortalShell>
  );
}

/** Diagonal repeating watermark overlay — pointer-events:none so the video still hits play. */
function WatermarkOverlay({ text }) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='340' height='200' viewBox='0 0 340 200'>
    <g fill='rgba(255,255,255,0.32)' font-family='Barlow Condensed, Arial, sans-serif' font-size='22' font-weight='700' letter-spacing='2' transform='rotate(-24 170 100)'>
      <text x='-40' y='40'>${text}</text>
      <text x='-40' y='100'>${text}</text>
      <text x='-40' y='160'>${text}</text>
    </g>
  </svg>`;
  const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: url,
        backgroundRepeat: "repeat",
        mixBlendMode: "screen",
      }}
      data-testid="watermark-overlay"
    />
  );
}

/** Preview video that auto-pauses at `previewSeconds` and hard-caps seeking. */
function GatedPreviewVideo({ src, previewSeconds, watermarkText, onLocked, controls = false, autoPlay = true }) {
  const ref = useRef(null);
  const [locked, setLocked] = useState(false);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    setLocked(false);
  }, [src]);

  useEffect(() => {
    if (ref.current) ref.current.playbackRate = speed;
  }, [speed]);

  const onTimeUpdate = () => {
    const v = ref.current;
    if (!v) return;
    if (v.currentTime >= previewSeconds && !locked) {
      v.pause();
      v.currentTime = Math.min(previewSeconds, v.duration || previewSeconds);
      setLocked(true);
      onLocked?.();
    }
  };

  const onSeeking = () => {
    const v = ref.current;
    if (v && v.currentTime > previewSeconds) {
      v.currentTime = previewSeconds;
    }
  };

  return (
    <div className="relative w-full h-full bg-black">
      <video
        ref={ref}
        src={src}
        muted
        autoPlay={autoPlay}
        controls={controls && !locked}
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onSeeking={onSeeking}
        onContextMenu={(e) => e.preventDefault()}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        className="w-full h-full object-contain"
      />
      <WatermarkOverlay text={watermarkText} />
      {locked && (
        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-center px-6" data-testid="preview-locked">
          <Lock size={28} className="text-orange-500 mb-2" />
          <div className="font-display uppercase text-xl font-bold">Preview ended</div>
          <div className="text-xs text-stone-400 mt-1">Buy this clip to watch and download the full run.</div>
        </div>
      )}
    </div>
  );
}

/** Playback controls: skip back / forward + slow-motion speed toggle. */
function PlaybackControls({ videoRef }) {
  const [speed, setSpeed] = useState(1);
  const speeds = [0.25, 0.5, 1, 1.5, 2];
  const applySpeed = (s) => {
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };
  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + delta));
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3 justify-center" data-testid="playback-controls">
      <button
        onClick={() => skip(-5)}
        className="btn-secondary !py-1.5 !px-3 text-[11px] inline-flex items-center gap-1"
        title="Back 5s"
        data-testid="skip-back-5"
      >
        « 5s
      </button>
      <button
        onClick={() => skip(-1)}
        className="btn-secondary !py-1.5 !px-3 text-[11px] inline-flex items-center gap-1"
        title="Back 1s"
        data-testid="skip-back-1"
      >
        « 1s
      </button>
      <div className="inline-flex border border-stone-800 overflow-hidden" data-testid="speed-picker">
        {speeds.map((s) => (
          <button
            key={s}
            onClick={() => applySpeed(s)}
            className={`px-2 py-1.5 text-[10px] font-mono-tc uppercase tracking-widest ${
              speed === s ? "bg-orange-600 text-white" : "text-stone-400 hover:text-stone-100"
            }`}
            data-testid={`speed-${s}`}
          >
            {s === 1 ? "1×" : `${s}×`}
          </button>
        ))}
      </div>
      <button
        onClick={() => skip(1)}
        className="btn-secondary !py-1.5 !px-3 text-[11px] inline-flex items-center gap-1"
        title="Forward 1s"
        data-testid="skip-fwd-1"
      >
        1s »
      </button>
      <button
        onClick={() => skip(5)}
        className="btn-secondary !py-1.5 !px-3 text-[11px] inline-flex items-center gap-1"
        title="Forward 5s"
        data-testid="skip-fwd-5"
      >
        5s »
      </button>
    </div>
  );
}

function ClipCard({ clip, pricing, priceLabel, busy, editing, hasCapCutAccess, canCoach, onPreview, onBuy, onShare, onEdit, onCapCut, onCoach }) {
  const title = [clip.rider_horse_name, clip.horse_name].filter(Boolean).join(" · ")
    || clip.sub_label || "Clip";
  const paid = clip.purchased;
  return (
    <div
      className="border border-stone-800 hover:border-orange-700 transition-colors group flex flex-col"
      data-testid={`my-clip-${clip.id}`}
    >
      <button
        type="button"
        onClick={paid ? onPreview : onBuy}
        className="block w-full aspect-video bg-black relative overflow-hidden"
        data-testid={`clip-thumb-${clip.id}`}
      >
        {clip.stream_url && (
          <video
            src={clip.stream_url}
            muted
            preload="metadata"
            className="w-full h-full object-cover"
          />
        )}
        {/* Always show watermark on the card thumbnail */}
        <WatermarkOverlay text={pricing.watermark_text} />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
          {paid ? <Play size={28} className="text-white" /> : <Lock size={24} className="text-white" />}
        </div>
        {clip.duration != null && (
          <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] bg-black/70 text-stone-100 px-1.5 py-0.5">
            {fmtDuration(clip.duration)}
          </div>
        )}
        {paid && (
          <div className="absolute top-1.5 left-1.5 font-mono-tc text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1" data-testid={`paid-badge-${clip.id}`}>
            <Check size={10} /> Purchased
          </div>
        )}
      </button>
      <div className="p-3 flex-1 flex flex-col">
        <div className="font-display uppercase font-bold truncate">{title}</div>
        <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap gap-x-2">
          {clip.category && (
            <span className="text-orange-400/80 uppercase tracking-widest">
              {CATEGORY_NAME[clip.category] || clip.category}
            </span>
          )}
          {clip.horse_name && <span>Horse · {clip.horse_name}</span>}
          {clip.heeler_name && <span>Heeler · {clip.heeler_name}</span>}
        </div>
        {clip.event_title && (
          <div className="text-[11px] text-stone-500 mt-1 truncate">{clip.event_title}</div>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 gap-2">
          <button
            onClick={onPreview}
            className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
            data-testid={`preview-my-clip-${clip.id}`}
          >
            <Play size={11} /> {paid ? "Watch" : "Preview"}
          </button>
          {paid ? (
            <>
              <button
                onClick={onShare}
                className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
                title="Share to social"
                data-testid={`share-my-clip-${clip.id}`}
              >
                <Share2 size={11} />
              </button>
              <button
                onClick={onEdit}
                disabled={editing}
                className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 hover:!text-orange-400 disabled:opacity-50"
                title="Copy into your Rodeo Box to trim / re-cut (Creator+)"
                data-testid={`edit-my-clip-${clip.id}`}
              >
                <Pencil size={11} /> {editing ? "…" : "Edit"}
              </button>
              <button
                onClick={onCapCut}
                className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 hover:!text-sky-400"
                title={hasCapCutAccess
                  ? "Save this clip and open CapCut to edit it there"
                  : "CapCut export unlocks with Creator or Pro"}
                data-testid={`capcut-my-clip-${clip.id}`}
              >
                {hasCapCutAccess ? <Film size={11} /> : <Lock size={11} />}
                CapCut
              </button>
              <button
                onClick={onCoach}
                className={`btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 ${canCoach ? "!text-orange-400" : ""}`}
                title={canCoach
                  ? "Record a coaching session on this clip (face + voice + timer)"
                  : "Coaching Studio unlocks with Pro"}
                data-testid={`coach-my-clip-${clip.id}`}
              >
                {canCoach ? <Presentation size={11} /> : <Lock size={11} />}
                Coach
              </button>
              <a
                href={`${BACKEND_URL}/api/clips/${clip.id}/download`}
                className="btn-primary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
                data-testid={`download-my-clip-${clip.id}`}
              >
                <Download size={11} /> Download
              </a>
            </>
          ) : (
            <button
              onClick={onBuy}
              disabled={busy}
              className="btn-primary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
              data-testid={`buy-my-clip-${clip.id}`}
            >
              <ShoppingCart size={11} />
              {busy ? "Redirecting…" : `Buy · ${priceLabel}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ clip, pricing, priceLabel, busy, onClose, onBuy }) {
  const title = [clip.rider_horse_name, clip.horse_name].filter(Boolean).join(" · ")
    || clip.sub_label || "Clip";
  const paid = clip.purchased;
  const videoRef = useRef(null);
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-start sm:items-center justify-center px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="my-clip-preview-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display uppercase font-bold text-xl">{title}</div>
            {clip.sub_label && clip.rider_horse_name && (
              <div className="text-sm text-orange-400/80">{clip.sub_label}</div>
            )}
          </div>
          <button onClick={onClose} className="text-stone-300 hover:text-white" data-testid="close-preview">
            <X size={20} />
          </button>
        </div>

        <div className="aspect-video w-full max-w-md mx-auto sm:max-w-lg">
          {paid ? (
            <video
              ref={videoRef}
              src={clip.stream_url}
              controls
              autoPlay
              className="w-full h-full bg-black"
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <GatedPreviewVideo
              src={clip.stream_url}
              previewSeconds={pricing.preview_seconds}
              watermarkText={pricing.watermark_text}
              autoPlay
            />
          )}
        </div>

        {paid && <PlaybackControls videoRef={videoRef} />}

        <div className="mt-4 flex flex-wrap items-center gap-2 justify-center">
          {paid ? (
            <a
              href={`${BACKEND_URL}/api/clips/${clip.id}/download`}
              className="btn-primary inline-flex items-center gap-2 text-xs"
              data-testid="download-preview-clip"
            >
              <Download size={12} /> Download clean MP4
            </a>
          ) : (
            <>
              <button
                onClick={onBuy}
                disabled={busy}
                className="btn-primary inline-flex items-center gap-2 text-sm px-6 py-3"
                data-testid="buy-preview-clip"
              >
                <ShoppingCart size={14} />
                {busy ? "Redirecting to Stripe…" : `Buy this clip · ${priceLabel}`}
              </button>
              <div className="text-[11px] text-stone-500 w-full text-center sm:w-auto sm:text-left">
                One-time purchase · instant download · no watermark
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Plan-picker banner — hero card at the top of the client welcome page.
// Free users see 3 side-by-side plan cards with prices + CTA.
// Paid users see a compact status pill with an "upgrade / manage" link.
// ─────────────────────────────────────────────────────────────────────
function PlanPickerBanner({ tier, subInfo, onGo }) {
  const isFree = !tier || tier === "free";
  const usedBytes = subInfo?.storage_used_bytes || 0;
  const limitBytes = subInfo?.storage_limit_bytes || 0;
  const pct = limitBytes > 0 ? Math.min(100, (usedBytes / limitBytes) * 100) : 0;
  const fmtGB = (b) => (b / (1024 ** 3)).toFixed(b >= 1024 ** 3 ? 1 : 2);
  const barColor = pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-orange-500";

  if (!isFree) {
    return (
      <div
        className="mb-8 border border-orange-700/50 bg-gradient-to-r from-orange-950/30 via-stone-950 to-stone-950 p-4 space-y-3"
        data-testid="plan-status-banner"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Sparkles size={18} className="text-orange-500" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Current plan</div>
              <div className="font-display uppercase text-lg font-semibold capitalize">Rodeo {tier}</div>
            </div>
          </div>
          <button
            onClick={onGo}
            className="btn-secondary text-xs inline-flex items-center gap-2 hover:!text-orange-400 hover:!border-orange-700"
            data-testid="plan-manage-btn"
          >
            {tier === "pro" ? "Manage plan" : "Upgrade plan"} <ArrowRight size={12} />
          </button>
        </div>
        {/* Storage usage bar */}
        {limitBytes > 0 && (
          <div data-testid="storage-usage-bar">
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <span className="uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5">
                <Shield size={11} /> Storage
              </span>
              <span className="font-mono-tc text-stone-300">
                {fmtGB(usedBytes)} GB <span className="text-stone-600">/ {fmtGB(limitBytes)} GB</span>
                <span className={`ml-2 ${pct >= 90 ? "text-red-400" : pct >= 75 ? "text-amber-400" : "text-stone-500"}`}>
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div className="h-2 bg-stone-900 border border-stone-800 overflow-hidden">
              <div
                className={`h-full transition-all ${barColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {pct >= 90 && (
              <div className="text-[11px] text-red-400 mt-1.5 inline-flex items-center gap-1">
                <Zap size={11} /> Almost full — delete old videos or upgrade to keep uploading.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  // Free — big attention-grabbing banner
  return (
    <div
      className="mb-10 border-2 border-orange-600 bg-gradient-to-br from-orange-950/40 via-stone-950 to-stone-950 p-5 md:p-6 relative overflow-hidden"
      data-testid="plan-picker-banner"
    >
      <div className="absolute -top-4 -right-4 w-40 h-40 bg-orange-600/10 blur-3xl pointer-events-none" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 relative">
        <div>
          <div className="inline-flex items-center gap-2 px-2 py-1 bg-orange-600 text-white text-[10px] uppercase tracking-widest font-bold mb-2">
            <Sparkles size={11} /> Unlock everything
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl font-bold leading-[0.95]">
            Choose your Rodeo plan
          </h2>
          <p className="text-stone-400 text-sm mt-2 max-w-xl">
            Store your own videos, get AI-clipped runs, and build reels in seconds.
            Pick a tier and start uploading today — cancel anytime.
          </p>
          <div className="text-[11px] text-stone-500 mt-2 inline-flex items-center gap-1.5">
            <Lock size={11} /> Free plan: uploads locked. Pick any tier below to start.
          </div>
        </div>
        <button
          onClick={onGo}
          className="btn-primary text-sm inline-flex items-center gap-2 self-start whitespace-nowrap"
          data-testid="plan-banner-cta"
        >
          Compare all plans <ArrowRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative">
        <PlanCard
          icon={Shield}
          name="Storage"
          price="10"
          tagline="Upload + share"
          bullets={["5 GB private storage", "Buy clips from studio", "View shared clips"]}
          onGo={onGo}
          testId="plan-card-storage"
        />
        <PlanCard
          icon={Sparkles}
          name="Creator"
          price="20"
          tagline="Most popular"
          highlight
          bullets={["30 GB storage", "Trim editor + one-click clips", "Reel builder + share sheet"]}
          onGo={onGo}
          testId="plan-card-creator"
        />
        <PlanCard
          icon={Zap}
          name="Pro"
          price="40"
          tagline="Full AI power"
          bullets={["100 GB storage", "AI Suggest Cuts + Deep Scan", "Rider tracking + advanced reels"]}
          onGo={onGo}
          testId="plan-card-pro"
        />
      </div>
    </div>
  );
}

function PlanCard({ icon: Icon, name, price, tagline, bullets, onGo, highlight, testId }) {
  return (
    <button
      onClick={onGo}
      className={`text-left p-4 border transition-all group ${
        highlight
          ? "border-orange-600 bg-orange-950/30 hover:bg-orange-950/50 shadow-[0_0_24px_rgba(234,88,12,0.15)]"
          : "border-stone-800 hover:border-orange-700 hover:bg-stone-900/60"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${
          highlight ? "text-orange-300" : "text-stone-500"
        }`}>
          <Icon size={11} /> Rodeo {name}
        </div>
        {highlight && (
          <span className="text-[9px] uppercase tracking-widest bg-orange-600 text-white px-1.5 py-0.5">
            {tagline}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="font-display text-4xl font-bold leading-none">${price}</span>
        <span className="text-stone-500 text-xs">/mo</span>
      </div>
      <ul className="space-y-1 mb-3">
        {bullets.map((b) => (
          <li key={b} className="text-xs text-stone-300 flex items-start gap-1.5">
            <Check size={11} className={`shrink-0 mt-0.5 ${highlight ? "text-orange-400" : "text-stone-500"}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${
        highlight ? "text-orange-400" : "text-stone-500 group-hover:text-orange-400"
      }`}>
        Choose {name} <ArrowRight size={10} />
      </div>
    </button>
  );
}

